import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { publicAPI } from '../services/api';
import { useCart } from '../context/CartContext';

const MEDIA_URL = process.env.REACT_APP_MEDIA_URL || 'http://localhost:5001';

function resolveMedia(url) {
  if (!url) return null;
  return url.startsWith('http') ? url : `${MEDIA_URL}${url}`;
}

export default function ServiceDetailPage() {
  const { slug }   = useParams();
  const navigate   = useNavigate();
  const { addItem } = useCart();

  const [service,  setService]  = useState(null);
  const [loading,  setLoading]  = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [related,  setRelated]  = useState([]);
  const [added,    setAdded]    = useState(false);

  useEffect(() => {
    setLoading(true);
    publicAPI.serviceBySlug(slug)
      .then(r => {
        setService(r.data?.data);
        setLoading(false);
        if (r.data?.data?.category_code) {
          publicAPI.services({ category: r.data.data.category_code })
            .then(res => setRelated((res.data?.data||[]).filter(s=>s.slug!==slug).slice(0,3)))
            .catch(()=>{});
        }
      })
      .catch(err => { if (err.response?.status===404) setNotFound(true); setLoading(false); });
  }, [slug]);

  if (loading) return <div style={{ minHeight:'80vh', display:'flex', alignItems:'center', justifyContent:'center', color:'var(--t-muted)' }}>Loading…</div>;
  if (notFound||!service) return (
    <div style={{ minHeight:'80vh', display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', gap:16 }}>
      <div style={{ fontSize:'3rem', opacity:0.2 }}>◈</div>
      <div style={{ color:'var(--t-muted)', fontSize:'1.1rem' }}>Service not found</div>
      <Link to="/marketplace" className="btn btn-outline">← Back to Services</Link>
    </div>
  );

  const src         = resolveMedia(service.cover_image_url || service.media_url);
  const deliverables = Array.isArray(service.deliverables) ? service.deliverables : [];
  const features     = Array.isArray(service.features)     ? service.features     : [];
  const isAvailable  = service.status === 'active';
  const hasPricing   = service.is_fixed_price && service.price;

  const handleAddToCart = () => {
    addItem({
      id:       service.id,
      name:     service.name,
      type:     'service',
      price:    Number(service.price),
      currency: service.currency || 'RWF',
      image:    service.media_url || service.cover_image_url,
      slug:     service.slug,
    });
    setAdded(true);
    setTimeout(() => setAdded(false), 2000);
  };

  const handleBuyNow = () => {
    handleAddToCart();
    navigate('/checkout');
  };

  return (
    <div>
      {/* Hero */}
      <section style={{ paddingTop:'calc(var(--nav-h) + clamp(40px,6vw,80px))', paddingBottom:'clamp(40px,6vw,80px)', background:'linear-gradient(160deg, var(--bg-1) 0%, var(--bg-0) 100%)', position:'relative', overflow:'hidden' }}>
        <div style={{ position:'absolute', inset:0, background:'radial-gradient(ellipse at 70% 30%, rgba(232,164,58,0.05) 0%, transparent 60%)', pointerEvents:'none' }}/>
        <div className="container" style={{ position:'relative', zIndex:1 }}>
          <Link to="/marketplace" style={{ fontSize:'0.82rem', color:'var(--t-muted)', textDecoration:'none', display:'inline-flex', alignItems:'center', gap:6, marginBottom:24 }}>← Back to Marketplace</Link>

          <div style={{ display:'grid', gridTemplateColumns:'minmax(0,1fr) 280px', gap:40, alignItems:'start' }}>
            <div>
              <div style={{ display:'flex', gap:8, marginBottom:16, flexWrap:'wrap' }}>
                <code style={{ fontSize:'0.75rem', fontWeight:700, letterSpacing:'0.1em', color:'var(--gold)', background:'rgba(232,164,58,0.1)', padding:'4px 10px', borderRadius:4 }}>{service.sku}</code>
                {service.category_name && <span style={{ padding:'4px 12px', borderRadius:20, fontSize:'0.72rem', background:'var(--bg-2)', color:'var(--t-muted)', border:'1px solid var(--border)' }}>{service.category_name}</span>}
                <span style={{ padding:'4px 12px', borderRadius:20, fontSize:'0.72rem', fontWeight:700, background:isAvailable?'rgba(62,207,142,0.12)':'rgba(107,114,128,0.12)', color:isAvailable?'#3ecf8e':'#6b7280', border:`1px solid ${isAvailable?'rgba(62,207,142,0.3)':'rgba(107,114,128,0.2)'}` }}>
                  {isAvailable ? '● Available' : '○ Coming Soon'}
                </span>
              </div>
              <h1 style={{ fontFamily:'var(--font-display)', fontSize:'clamp(1.8rem,4vw,2.6rem)', fontWeight:700, lineHeight:1.2, marginBottom:16 }}>{service.name}</h1>
              {service.description && <p style={{ fontSize:'1rem', color:'var(--t-secondary)', lineHeight:1.8, maxWidth:620, marginBottom:24 }}>{service.description}</p>}
              {service.problem_solved && (
                <div style={{ padding:'16px 20px', background:'rgba(232,164,58,0.06)', border:'1px solid rgba(232,164,58,0.2)', borderRadius:'var(--r-md)', borderLeft:'3px solid var(--gold)', maxWidth:560 }}>
                  <div style={{ fontSize:'0.65rem', color:'var(--gold)', textTransform:'uppercase', letterSpacing:'0.08em', fontWeight:700, marginBottom:6 }}>Problem We Solve</div>
                  <p style={{ fontSize:'0.9rem', color:'var(--t-secondary)', lineHeight:1.7 }}>{service.problem_solved}</p>
                </div>
              )}
            </div>

            {/* CTA card */}
            <div style={{ background:'var(--bg-2)', border:'1px solid var(--border)', borderRadius:'var(--r-xl)', padding:24 }}>
              {src && (
                <div style={{ height:140, borderRadius:'var(--r-md)', overflow:'hidden', marginBottom:20 }}>
                  <img src={src} alt={service.name} style={{ width:'100%', height:'100%', objectFit:'cover' }} onError={e=>e.target.parentNode.style.display='none'}/>
                </div>
              )}

              {/* Price display */}
              {hasPricing && (
                <div style={{ marginBottom:16 }}>
                  <div style={{ fontSize:'0.68rem', color:'var(--t-muted)', textTransform:'uppercase', letterSpacing:'0.08em', marginBottom:4 }}>Fixed Price</div>
                  <div style={{ fontSize:'1.6rem', fontWeight:700, color:'var(--gold)', fontFamily:'var(--font-display)' }}>
                    {service.currency||'RWF'} {Number(service.price).toLocaleString()}
                  </div>
                </div>
              )}

              <div style={{ fontSize:'0.78rem', color:'var(--t-muted)', marginBottom:16, lineHeight:1.6 }}>
                {hasPricing
                  ? 'Fixed-price service — add to cart and checkout securely.'
                  : isAvailable ? 'Ready to start? Get in touch for a custom quote.' : 'This service is coming soon.'}
              </div>

              <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
                {hasPricing ? (
                  <>
                    <button onClick={handleAddToCart}
                      style={{ display:'flex', alignItems:'center', justifyContent:'center', gap:8, padding:'12px', borderRadius:10, border:`2px solid var(--gold)`, background:added?'var(--gold)':'rgba(232,164,58,0.08)', color:added?'#080a0d':'var(--gold)', fontWeight:700, fontSize:'0.9rem', cursor:'pointer', transition:'all 0.2s', fontFamily:'inherit' }}>
                      {added ? '✓ Added to Cart!' : '🛒 Add to Cart'}
                    </button>
                    <button onClick={handleBuyNow}
                      style={{ display:'flex', alignItems:'center', justifyContent:'center', padding:'12px', borderRadius:10, border:'none', background:'var(--gold)', color:'#080a0d', fontWeight:700, fontSize:'0.9rem', cursor:'pointer', transition:'all 0.15s', fontFamily:'inherit' }}
                      onMouseEnter={e=>e.currentTarget.style.filter='brightness(1.1)'}
                      onMouseLeave={e=>e.currentTarget.style.filter='none'}>
                      Buy Now →
                    </button>
                  </>
                ) : (
                  <Link to={`/contact?type=client&service=${service.slug}`}
                    className="btn btn-primary" style={{ width:'100%', justifyContent:'center', display:'flex' }}>
                    {isAvailable ? 'Request This Service' : 'Express Interest'}
                  </Link>
                )}
                <Link to="/contact" style={{ display:'block', textAlign:'center', fontSize:'0.8rem', color:'var(--t-muted)', textDecoration:'none' }}>Ask a question →</Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Cover image */}
      {src && <div style={{ height:'clamp(140px,18vw,220px)', overflow:'hidden' }}><img src={src} alt={service.name} style={{ width:'100%', height:'100%', objectFit:'cover', display:'block' }}/></div>}

      {/* Body */}
      <section style={{ padding:'clamp(40px,6vw,80px) 0' }}>
        <div className="container" style={{ display:'grid', gridTemplateColumns:'minmax(0,1fr) 280px', gap:48, alignItems:'start' }}>
          <div style={{ display:'flex', flexDirection:'column', gap:48 }}>
            {service.scope && (
              <div>
                <h2 style={{ fontFamily:'var(--font-display)', fontSize:'1.3rem', fontWeight:700, marginBottom:16, color:'var(--gold)' }}>Scope of Work</h2>
                <p style={{ fontSize:'0.95rem', color:'var(--t-secondary)', lineHeight:1.8 }}>{service.scope}</p>
              </div>
            )}
            {features.length > 0 && (
              <div>
                <h2 style={{ fontFamily:'var(--font-display)', fontSize:'1.3rem', fontWeight:700, marginBottom:16 }}>What's Included</h2>
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>
                  {features.map((f,i)=>(
                    <div key={i} style={{ display:'flex', gap:10, alignItems:'flex-start', padding:'12px 14px', background:'var(--bg-2)', borderRadius:8, border:'1px solid var(--border)' }}>
                      <span style={{ color:'#3ecf8e', flexShrink:0, marginTop:1 }}>✓</span>
                      <span style={{ fontSize:'0.88rem', color:'var(--t-secondary)', lineHeight:1.5 }}>{typeof f==='string'?f:f.name||f.title||''}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {deliverables.length > 0 && (
              <div>
                <h2 style={{ fontFamily:'var(--font-display)', fontSize:'1.3rem', fontWeight:700, marginBottom:16 }}>Deliverables</h2>
                <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
                  {deliverables.map((d,i)=>(
                    <div key={i} style={{ display:'flex', gap:12, alignItems:'flex-start', padding:'10px 14px', background:'var(--bg-2)', borderRadius:8 }}>
                      <span style={{ width:22, height:22, borderRadius:'50%', background:'rgba(232,164,58,0.12)', border:'1px solid rgba(232,164,58,0.3)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:'0.7rem', fontWeight:700, color:'var(--gold)', flexShrink:0 }}>{i+1}</span>
                      <span style={{ fontSize:'0.88rem', color:'var(--t-secondary)', lineHeight:1.5 }}>{typeof d==='string'?d:d.name||d.title||''}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {Array.isArray(service.optional_addons) && service.optional_addons.length > 0 && (
              <div>
                <h2 style={{ fontFamily:'var(--font-display)', fontSize:'1.3rem', fontWeight:700, marginBottom:16 }}>Optional Add-ons</h2>
                <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
                  {service.optional_addons.map((addon,i)=>(
                    <div key={i} style={{ display:'flex', gap:12, alignItems:'flex-start', padding:'12px 16px', background:'var(--bg-2)', borderRadius:8, border:'1px solid var(--border)' }}>
                      <span style={{ color:'var(--gold)', flexShrink:0, marginTop:2 }}>+</span>
                      <span style={{ fontSize:'0.88rem', color:'var(--t-secondary)', lineHeight:1.5 }}>{typeof addon==='string'?addon:addon.name||addon.title||''}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div style={{ display:'flex', flexDirection:'column', gap:16, position:'sticky', top:'calc(var(--nav-h) + 24px)' }}>
            <div style={{ background:'var(--bg-2)', border:'1px solid var(--border)', borderRadius:'var(--r-lg)', padding:18 }}>
              <div style={{ fontFamily:'var(--font-display)', fontWeight:600, fontSize:'0.9rem', marginBottom:14 }}>Service Info</div>
              <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
                {[
                  ['SKU',      service.sku],
                  ['Category', service.category_name],
                  ['Status',   isAvailable?'Available':'Coming Soon'],
                  ['Pricing',  hasPricing ? `${service.currency||'RWF'} ${Number(service.price).toLocaleString()}` : 'Custom quote'],
                  ['Timeline', service.estimated_timeline],
                ].filter(([,v])=>v).map(([k,v])=>(
                  <div key={k} style={{ display:'flex', justifyContent:'space-between', fontSize:'0.82rem' }}>
                    <span style={{ color:'var(--t-muted)' }}>{k}</span>
                    <span style={{ fontWeight:500 }}>{v}</span>
                  </div>
                ))}
              </div>
            </div>

            {hasPricing ? (
              <button onClick={handleAddToCart}
                style={{ display:'flex', alignItems:'center', justifyContent:'center', gap:8, padding:'12px', borderRadius:10, border:`2px solid var(--gold)`, background:added?'var(--gold)':'rgba(232,164,58,0.08)', color:added?'#080a0d':'var(--gold)', fontWeight:700, fontSize:'0.9rem', cursor:'pointer', transition:'all 0.2s', fontFamily:'inherit', width:'100%' }}>
                {added ? '✓ Added to Cart!' : '🛒 Add to Cart'}
              </button>
            ) : (
              <Link to={`/contact?type=client&service=${service.slug}`}
                className="btn btn-primary" style={{ width:'100%', justifyContent:'center', display:'flex' }}>
                Request This Service
              </Link>
            )}

            {related.length > 0 && (
              <div style={{ background:'var(--bg-2)', border:'1px solid var(--border)', borderRadius:'var(--r-lg)', overflow:'hidden' }}>
                <div style={{ padding:'12px 16px', borderBottom:'1px solid var(--border)', fontSize:'0.82rem', fontWeight:600, color:'var(--t-muted)' }}>Related Services</div>
                {related.map(r=>(
                  <Link key={r.id} to={`/marketplace/services/${r.slug}`}
                    style={{ display:'block', padding:'12px 16px', borderBottom:'1px solid var(--border)', textDecoration:'none', transition:'background 0.15s' }}
                    onMouseEnter={e=>e.currentTarget.style.background='var(--bg-hover)'}
                    onMouseLeave={e=>e.currentTarget.style.background='transparent'}>
                    <div style={{ fontSize:'0.85rem', fontWeight:500, color:'var(--t-primary)', marginBottom:2 }}>{r.name}</div>
                    <code style={{ fontSize:'0.65rem', color:'var(--gold)' }}>{r.sku}</code>
                  </Link>
                ))}
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Bottom CTA */}
      <section style={{ background:'var(--bg-1)', borderTop:'1px solid var(--border)', padding:'clamp(40px,5vw,60px) 0' }}>
        <div className="container" style={{ display:'flex', alignItems:'center', justifyContent:'space-between', gap:24, flexWrap:'wrap' }}>
          <div>
            <h2 style={{ fontFamily:'var(--font-display)', fontSize:'1.4rem', fontWeight:700, marginBottom:8 }}>Ready to get started?</h2>
            <p style={{ color:'var(--t-secondary)', fontSize:'0.92rem' }}>Let's talk about your project and how we can help.</p>
          </div>
          <div style={{ display:'flex', gap:12 }}>
            {hasPricing
              ? <button onClick={handleBuyNow} className="btn btn-primary">Buy Now →</button>
              : <Link to={`/contact?type=client&service=${service.slug}`} className="btn btn-primary">Start a Project →</Link>
            }
            <Link to="/marketplace" className="btn btn-outline">Browse All Services</Link>
          </div>
        </div>
      </section>
    </div>
  );
}